import { localTzToUTC } from "@/components/psychologist/calendar/timeZoneHelpers";
import apiCoreAdapter from "@/hooks/apiCoreAdapter";
import { PsychologistFreeScheduleStatus } from "@/components/SessionDebug";
import { addDaysIso } from "@/utils/holidays";
import { findOccupyingSessions, OccupyingSession } from "./formacionBlock";

/** El día completo de la psicóloga: de 00:00 a 24:00 de SU hora, en instantes UTC. */
function dayRangeMs(dateIso: string, timezone: string): { startMs: number; endMs: number } {
  return {
    startMs: localTzToUTC(dateIso, "00:00", timezone).getTime(),
    endMs: localTzToUTC(addDaysIso(dateIso, 1), "00:00", timezone).getTime(),
  };
}

/**
 * Sesiones (no canceladas) de un día, en la zona horaria de la psicóloga — así una psico en
 * Italia ve las sesiones de SU día, no del día en Argentina. Lo usan "Bloquear mi agenda" del
 * aviso de feriado (Comunidad) y el aviso de feriados de la Agenda. Solo consulta: nunca cancela
 * ni modifica una sesión. Lanza si no se pudo consultar.
 */
export async function sessionsOnHoliday(dateIso: string, timezone: string): Promise<OccupyingSession[]> {
  const { startMs, endMs } = dayRangeMs(dateIso, timezone);
  return findOccupyingSessions(startMs, endMs);
}

/** Lo que cuenta como "no trabaja ese tramo": bloqueo manual o vacaciones. */
const BLOCKED = new Set<string>([
  PsychologistFreeScheduleStatus.OCCUPIED_MANUAL_BY_PSYCHOLOGIST,
  PsychologistFreeScheduleStatus.OCCUPIED_BY_VACATION,
]);

/**
 * ¿Ya bloqueó el día (en su zona horaria)? Sí cuando hay algún tramo bloqueado (o de
 * vacaciones) y no queda NINGÚN horario libre para agendar. No se exige que cada media hora
 * esté bloqueada: el bloqueo de "día completo" llega hasta las 23:59 y la última media hora
 * puede no figurar como bloqueada — con esa regla, el día bloqueado seguía mostrando el aviso.
 * Un bloqueo de una parte del día no cuenta mientras quede otro horario libre. Lanza si no se
 * pudo consultar.
 */
export async function isHolidayFullyBlocked(dateIso: string, timezone: string): Promise<boolean> {
  const { startMs, endMs } = dayRangeMs(dateIso, timezone);
  const slots = await apiCoreAdapter.psychologistAvailability({
    from: new Date(startMs).toISOString(),
    to: new Date(endMs).toISOString(),
    source: "psi_platform",
  });
  const delDia = (Array.isArray(slots) ? slots : []).filter((s) => {
    const t = new Date(s.slot).getTime();
    return t >= startMs && t < endMs;
  });
  const hayBloqueo = delDia.some((s) => BLOCKED.has(s.status));
  const hayLibre = delDia.some((s) => s.status === PsychologistFreeScheduleStatus.AVAILABLE);
  return hayBloqueo && !hayLibre;
}
